/**
 * @fileoverview Runner Result Overlay Component
 * @description Shows game result with animation
 */

import React, { memo, useEffect, useState, useRef } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Dimensions,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { getLottieSource } from "@/lib/lottie-registry";
import { RunnerResult } from "../types";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

interface RunnerResultOverlayProps {
  result: RunnerResult;
  onPlayAgain: () => void;
  onGoBack: () => void;
  /** Lottie key for new high score */
  highScoreLottieKey?: string;
  /** Lottie key for regular game over */
  gameOverLottieKey?: string;
}

const DEFAULT_HIGH_SCORE_LOTTIE = "trophy";
const DEFAULT_GAME_OVER_LOTTIE = "Partying_Emoji";

/**
 * Format number with commas
 */
const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

/**
 * Format time as mm:ss
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * RunnerResultOverlay Component
 */
export const RunnerResultOverlay = memo<RunnerResultOverlayProps>(
  ({
    result,
    onPlayAgain,
    onGoBack,
    highScoreLottieKey = DEFAULT_HIGH_SCORE_LOTTIE,
    gameOverLottieKey = DEFAULT_GAME_OVER_LOTTIE,
  }) => {
    const colors = useThemeColors();
    const [showContent, setShowContent] = useState(false);

    // Animation values - use useRef to persist across renders
    const overlayOpacity = useRef(new Animated.Value(0)).current;
    const contentScale = useRef(new Animated.Value(0.8)).current;

    // Get Lottie source
    const lottieKey = result.isNewHighScore ? highScoreLottieKey : gameOverLottieKey;
    const lottieSource = getLottieSource(lottieKey);

    useEffect(() => {
      // Reset animation values
      overlayOpacity.setValue(0);
      contentScale.setValue(0.8);

      // Animate in
      Animated.parallel([
        Animated.timing(overlayOpacity, {
          toValue: 1,
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.spring(contentScale, {
          toValue: 1,
          tension: 50,
          friction: 8,
          useNativeDriver: true,
        }),
      ]).start(() => {
        setShowContent(true);
      });
    }, [overlayOpacity, contentScale]);

    return (
      <Animated.View style={[styles.overlay, { opacity: overlayOpacity }]}>
        <Animated.View
          style={[
            styles.content,
            { backgroundColor: colors.surface, transform: [{ scale: contentScale }] },
          ]}
        >
          {/* Lottie Animation */}
          {lottieSource && (
            <View style={styles.lottieContainer}>
              <LottieView
                source={lottieSource}
                autoPlay
                loop={false}
                style={styles.lottie}
              />
            </View>
          )}

          {/* Title */}
          <View style={styles.titleSection}>
            {result.isNewHighScore ? (
              <>
                <Text style={[styles.title, { color: "#F59E0B" }]}>
                  🎉 Nieuw Record!
                </Text>
                <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
                  Je hebt je eigen record verbroken!
                </Text>
              </>
            ) : (
              <>
                <Text style={[styles.title, { color: colors.text }]}>
                  Game Over
                </Text>
                <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
                  Goed geprobeerd!
                </Text>
              </>
            )}
          </View>

          {/* Score */}
          <View style={[styles.scoreCard, { backgroundColor: colors.surfaceSecondary }]}>
            <Text style={[styles.scoreLabel, { color: colors.textSecondary }]}>
              Score
            </Text>
            <Text style={[styles.scoreValue, { color: colors.text }]}>
              {formatNumber(result.score)}
            </Text>
          </View>

          {/* Stats Grid */}
          <View style={styles.statsGrid}>
            <View style={[styles.statCard, { backgroundColor: colors.surfaceSecondary }]}>
              <Ionicons name="time" size={20} color={colors.primary} />
              <Text style={[styles.statValue, { color: colors.text }]}>
                {formatTime(result.timeSeconds)}
              </Text>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Tijd
              </Text>
            </View>

            <View style={[styles.statCard, { backgroundColor: colors.surfaceSecondary }]}>
              <Ionicons name="flash" size={20} color="#F59E0B" />
              <Text style={[styles.statValue, { color: colors.text }]}>
                {result.obstaclesAvoided}
              </Text>
              <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                Obstakels
              </Text>
            </View>

            {result.pointsEarned > 0 && (
              <View style={[styles.statCard, { backgroundColor: colors.surfaceSecondary }]}>
                <Ionicons name="star" size={20} color="#22C55E" />
                <Text style={[styles.statValue, { color: "#22C55E" }]}>
                  +{result.pointsEarned}
                </Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                  Punten
                </Text>
              </View>
            )}
          </View>

          {/* Buttons */}
          <View style={styles.buttonsContainer}>
            <TouchableOpacity
              onPress={onPlayAgain}
              style={[styles.playAgainButton, { backgroundColor: colors.primary }]}
              accessibilityRole="button"
              accessibilityLabel="Opnieuw spelen"
            >
              <Ionicons name="refresh" size={20} color="#FFFFFF" />
              <Text style={styles.playAgainText}>Opnieuw</Text>
            </TouchableOpacity>

            <TouchableOpacity
              onPress={onGoBack}
              style={[styles.backButton, { backgroundColor: colors.surfaceSecondary }]}
              accessibilityRole="button"
              accessibilityLabel="Terug naar lobby"
            >
              <Ionicons name="arrow-back" size={20} color={colors.text} />
              <Text style={[styles.backButtonText, { color: colors.text }]}>
                Terug
              </Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </Animated.View>
    );
  }
);

RunnerResultOverlay.displayName = "RunnerResultOverlay";

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.8)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
    zIndex: 100,
  },
  content: {
    width: "100%",
    maxWidth: 360,
    borderRadius: 24,
    padding: 24,
    alignItems: "center",
    gap: 16,
  },
  lottieContainer: {
    width: 120,
    height: 120,
  },
  lottie: {
    width: "100%",
    height: "100%",
  },
  titleSection: {
    alignItems: "center",
    gap: 4,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
  },
  subtitle: {
    fontSize: 14,
  },
  scoreCard: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 16,
    alignItems: "center",
    width: "100%",
  },
  scoreLabel: {
    fontSize: 12,
    fontWeight: "500",
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  scoreValue: {
    fontSize: 48,
    fontWeight: "bold",
    fontVariant: ["tabular-nums"],
  },
  statsGrid: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
  },
  statCard: {
    flex: 1,
    padding: 12,
    borderRadius: 12,
    alignItems: "center",
    gap: 4,
  },
  statValue: {
    fontSize: 18,
    fontWeight: "700",
  },
  statLabel: {
    fontSize: 11,
  },
  buttonsContainer: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
    marginTop: 8,
  },
  playAgainButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 12,
    gap: 8,
  },
  playAgainText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  backButton: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 12,
    gap: 8,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: "600",
  },
});

